import React from 'react';
import RowDeleteButton from '../../components/ActionButtons/RowDeleteButton';
import RowEditButton from '../../components/ActionButtons/RowEditButton';
import RowStartButton from '../../components/ActionButtons/RowStartButton';
import RowStopButton from '../../components/ActionButtons/RowStopButton';
import RowLogButton from '../../components/ActionButtons/RowLogButton';
import RowRunButton from '../../components/ActionButtons/RowRunButton';
import AutomationIcon from '../../components/AutomationIcon/AutomationIcon';
import StrategyUsageBadge from '../../components/StrategyUsageBadge/StrategyUsageBadge';
import HealthDot from '../../components/HealthDot/HealthDot';
import { isGridAutomation, isScheduleAutomation } from '../../utils/AutomationUtils';


/**
 * props:
 * - data
 * - usages: [{ id, name }] (opcional)
 * - onEditClick
 * - onStopClick
 * - onStartClick
 * - onDeleteClick
 * - onPromoteClick
 * - onCopyToTestClick
 * - onLogsClick
 */
function AutomationRow(props) {

    function getStatusClass(automation) {
        if (isScheduleAutomation(automation))
            return automation.isActive ? 'text-info' : 'text-danger';
        else
            return automation.isActive ? 'text-success' : 'text-danger';
    }

    function getStatusText(automation) {
        if (isScheduleAutomation(automation))
            return automation.isActive ? 'AGENDADA' : 'PARADA';
        else
            return automation.isActive ? 'ATIVA' : 'PARADA';
    }

    function getActionBadge(action) {
        const ic = (path, color, size = 14) => (
            <svg width={size} height={size} fill={color} viewBox='0 0 16 16'
                style={{ marginRight: 5, verticalAlign: 'middle', flexShrink: 0 }}>
                <path d={path} />
            </svg>
        );

        // Bootstrap Icons paths
        const ARROW_UP   = 'M16 8A8 8 0 1 0 0 8a8 8 0 0 0 16 0zm-7.5 3.5a.5.5 0 0 1-1 0V5.707L5.354 7.854a.5.5 0 1 1-.708-.708l3-3a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 5.707V11.5z';
        const ARROW_DOWN  = 'M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8.5 4.5a.5.5 0 0 0-1 0v5.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3a.5.5 0 0 0 .708 0l3-3a.5.5 0 1 0-.708-.708L8.5 10.293V4.5z';
        // Placa octagonal de STOP (Bootstrap bi-sign-stop-fill)
        const STOP_SQUARE = 'M9.05.435c-.58-.58-1.52-.58-2.1 0L.436 6.95c-.58.58-.58 1.519 0 2.098l6.516 6.516c.58.58 1.519.58 2.098 0l6.516-6.516c.58-.58.58-1.519 0-2.098L9.05.435zM4 6h8v4H4V6z';
        const TRAILING_IC = 'M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z';

        switch (action.type) {
            case 'ORDER': {
                if (!action.orderTemplate) return <>{ic(ARROW_UP, '#6c757d')} Ordem</>;
                const tpl = action.orderTemplate;
                const name = tpl.name || '';
                const isHardStop = name.toLowerCase().includes('hard stop') || name.toLowerCase().includes('stop');
                if (tpl.side === 'BUY')
                    return <>{ic(ARROW_UP, '#198754')} {name}</>;
                if (isHardStop)
                    return <>{ic(STOP_SQUARE, '#dc3545')} {name}</>;
                return <>{ic(ARROW_DOWN, '#dc3545')} {name}</>;
            }
            case 'TRAILING':
                return <>{ic(TRAILING_IC, '#fd7e14')} {action.orderTemplate?.name || 'Trailing'}</>;
            case 'AI_ANALYSIS':
                return <>🤖 Análise IA</>;
            case 'AI_MARKET_SCAN':
                return <>🔍 Scanner</>;
            case 'AI_ALERT':
                return <>🔔 Alerta IA</>;
            case 'ALERT_EMAIL':
                return <>✉ Email</>;
            case 'ALERT_SMS':
                return <>💬 SMS</>;
            case 'ALERT_TELEGRAM':
                return <>✈ Telegram</>;
            case 'GRID':
                return <>▦ Grid</>;
            case 'WITHDRAW':
                return <>↑ Saque</>;
            case 'ENABLE_AUTOMATION':
                return <>▶ {action.targetAutomationName || 'Ativar'}</>;
            case 'DISABLE_AUTOMATION':
                return <>⏹ {action.targetAutomationName || 'Parar'}</>;
            default:
                return <>{action.type}</>;
        }
    }

    // Observabilidade: rótulo "qual IA cuida desta automação" (vem de props.health.ai).
    // Em SHARDED, mostra o provedor "dono" determinístico do ativo; coringa (*USDT) varia por
    // ativo. Mostra também o último que de fato atendeu, sinalizando quando houve fallback.
    function getAiBadge() {
        const ai = props.health && props.health.ai;
        if (!ai) return null;
        const label = ai.variesByAsset ? 'varia por ativo' : (ai.provider || '—');
        const fallbackHit = ai.lastProvider && ai.provider && ai.lastProvider !== ai.provider;
        const titleParts = [];
        if (ai.mode === 'SHARDED' && ai.sharded)
            titleParts.push(`Sharded: ${ai.providerCount} provedores, 1 por ativo (capacidade somada).`);
        else
            titleParts.push(`Primário + fallback (${ai.providerCount} provedor(es)).`);
        if (ai.lastProvider)
            titleParts.push(`Último que atendeu: ${ai.lastProvider}${ai.lastModel ? ' (' + ai.lastModel + ')' : ''}${ai.lastSignal ? ' → ' + ai.lastSignal : ''}.`);
        if (fallbackHit) titleParts.push('Fallback acionado (atendeu provedor diferente do dono).');
        return (
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 2 }}>
                <span className='badge bg-light text-dark border' style={{ fontWeight: 'normal' }} title={titleParts.join(' ')}>
                    🧠 {label}
                    {ai.mode === 'SHARDED' && ai.sharded && <span className='text-muted ms-1'>·shard</span>}
                </span>
                {fallbackHit && (
                    <span className='badge bg-warning text-dark ms-1' title={`Atendido por ${ai.lastProvider} (fallback)`}>
                        ↪ {ai.lastProvider}
                    </span>
                )}
            </div>
        );
    }

    function getEditModal() {
        if (!props.data.actions[0]) return 'modalAutomation';

        return isGridAutomation(props.data) ? 'modalGrid' : 'modalAutomation';
    }

    return (
        <tr className='align-middle'>
            <td>
                <HealthDot health={props.health} />
                <AutomationIcon automation={props.data} />
                {props.data.symbol}
            </td>
            <td>
                {props.data.name}
                <StrategyUsageBadge usages={props.usages} />
            </td>

            <td>
                <span className={getStatusClass(props.data)}>
                    {getStatusText(props.data)}
                </span>
                {props.data.environment === 'test' && (
                    <span className='badge bg-warning text-dark ms-2' title='Modo simulação — ordens não executam na corretora real'>TESTE</span>
                )}
            </td>
            <td style={{ fontSize: 12 }}>
                {(props.data.actions || []).map((action, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center' }}>{getActionBadge(action)}</div>
                ))}
                {getAiBadge()}
            </td>
            <td>
                <RowEditButton id={props.data.id} title='Editar esta Automação' modalId={getEditModal()} onClick={props.onEditClick} />
                <RowStopButton id={props.data.id} title='Parar esta Automação' onClick={props.onStopClick} showButton={props.data.isActive} />
                <RowStartButton id={props.data.id} title='Iniciar esta Automação' onClick={props.onStartClick} showButton={!props.data.isActive} />
                <RowRunButton id={props.data.id} title='Disparar agora' onClick={props.onRunClick} showButton={isScheduleAutomation(props.data) && props.data.isActive} />
                <span style={props.data.logs ? {} : { opacity: 0.35 }}
                    title={props.data.logs ? '' : 'Logs desabilitados — ative "Habilitar Logs?" na automação'}>
                    <RowLogButton id={props.data.id} title='Ver logs desta Automação'
                        modalId='modalAutomationLogs' onClick={props.onLogsClick} showButton={true} />
                </span>
                <RowDeleteButton id={props.data.id} title='Excluir esta Automação' onClick={props.onDeleteClick} showButton={!props.data.isActive} />
                {props.health && props.health.aiUncovered && props.health.aiUncovered.length > 0 && (
                    <button
                        type='button'
                        data-symbol={props.health.aiUncovered[0]}
                        className='btn btn-sm btn-outline-warning ms-1 py-0 px-2'
                        style={{ fontSize: '0.75em' }}
                        title={`Sem produtor de sinal de IA para ${props.health.aiUncovered[0]}. Provisiona uma Análise IA (consome tokens) para o short por IA poder agir. A Regra já cobre por regime sem IA.`}
                        onClick={props.onProvisionAiClick}>
                        🤖 Provisionar IA
                    </button>
                )}
                {props.data.environment === 'test' && (
                    <button
                        data-id={props.data.id}
                        type='button'
                        className='btn btn-sm btn-outline-success ms-1 py-0 px-2'
                        style={{ fontSize: '0.75em' }}
                        title='Promover esta automação de teste para produção (cria cópia em produção e desativa a versão de teste)'
                        onClick={props.onPromoteClick}>
                        🟢 Promover
                    </button>
                )}
                {props.data.environment === 'production' && (
                    <button
                        data-id={props.data.id}
                        type='button'
                        className='btn btn-sm btn-outline-warning ms-1 py-0 px-2'
                        style={{ fontSize: '0.75em' }}
                        title='Criar cópia desta automação no ambiente de Teste (desativada — ative quando quiser)'
                        onClick={props.onCopyToTestClick}>
                        🧪 Testar
                    </button>
                )}
            </td>
        </tr>
    );
}

export default AutomationRow;